import React, { useMemo, useState, useRef } from 'react';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { useEffect } from 'react';
import { CustomLegend, formatKWh, CustomStackedBarTooltip } from './ChartUtils';

const HeatingEnergyByDevice = ({ data }) => {
  const [activeData, setActiveData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartRef = useRef(null);
  
  const processedData = useMemo(() => {
    if (!data?.heating_summary) return [];
    
    // Convert object to array and ensure all entries have the same properties
    const entries = Object.entries(data.heating_summary);
    
    // Find all unique device keys across all dates
    const allDeviceKeys = new Set();
    entries.forEach(([_, values]) => {
      const { total_consumption, total_cost, outdoor_temp_avg, ...deviceData } = values;
      Object.keys(deviceData).forEach(key => {
        if (!key.includes('Configuration')) {
          allDeviceKeys.add(key);
        }
      });
    });
    
    // Create processed data with consistent properties
    return entries.map(([date, values]) => {
      const { total_consumption, total_cost, outdoor_temp_avg, ...deviceData } = values;
      
      // Start with date
      const result = { date };
      
      // Add all possible device keys with default value 0
      allDeviceKeys.forEach(key => {
        result[key] = 0;
      });
      
      // Override with actual values
      Object.entries(deviceData).forEach(([key, value]) => {
        if (!key.includes('Configuration')) {
          result[key] = Number(value) || 0;
        }
      });
      
      // Add metadata
      result.total_consumption = total_consumption || 0;
      result.outdoor_temp_avg = outdoor_temp_avg || 0;
      
      return result;
    }).sort((a, b) => a.date.localeCompare(b.date)); // Sort by date
  }, [data]);

  const deviceKeys = useMemo(() => {
    if (!processedData.length) return [];
    
    // Get all keys except date and metadata
    const excludeKeys = ['date', 'total_consumption', 'total_cost', 'outdoor_temp_avg'];
    const keys = Object.keys(processedData[0]).filter(key => 
      !excludeKeys.includes(key) && !key.includes('Configuration')
    );
    
    // Filter to only include devices that have some consumption
    return keys.filter(key => {
      const hasConsumption = processedData.some(item => item[key] > 0);
      return hasConsumption;
    });
  }, [processedData]);

  // Use the COLORS from ChartUtils for consistency
  const COLORS = [
    '#4285F4', '#F39C12', '#8758FF', '#00C9A7', '#FF6D28', 
    '#FF1E56', '#1ABC9C', '#9B59B6', '#3498DB', '#D35400'
  ];
  
  // Create a color map to keep track of which color is assigned to each data series
  const colorMap = useMemo(() => {
    const map = {};
    deviceKeys.forEach((device, index) => {
      map[device] = COLORS[index % COLORS.length];
    });
    return map;
  }, [deviceKeys]);

  const handleMouseMove = (props) => {
    if (props.activePayload && props.activePayload.length > 0) {
      setActiveData(props.activePayload[0].payload);
    }
  };

  const handleMouseLeave = () => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
  };

  useEffect(() => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
    
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };
    
    // Initial update after a short delay to ensure chart is rendered
    const timer = setTimeout(updateWidth, 100);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, [processedData]);

  // Debug output
  useEffect(() => {
    console.log('Processed Data:', processedData);
    console.log('Device Keys:', deviceKeys);
  }, [processedData, deviceKeys]);

  if (!processedData.length) {
    console.log('No processed data available');
    return null;
  }

  return (
    <div ref={chartRef}>
      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height={400}>
          <BarChart 
            data={processedData}
            margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              angle={-45}
              textAnchor="end"
              height={60}
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
              tickFormatter={(value) => formatKWh(value)}
            />
            
            <Tooltip 
              content={<CustomStackedBarTooltip />}
              cursor={{ stroke: '#ccc', strokeWidth: 1 }} 
            />
            
            {deviceKeys.map((key) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="a"
                fill={colorMap[key]}
                name={key}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {containerWidth > 0 && activeData && (
        <div className="mt-4">
          <CustomLegend 
            payload={deviceKeys.map(key => ({ dataKey: key, name: key }))}
            colors={deviceKeys.map(key => colorMap[key])}
            colorMap={colorMap}
            activeData={activeData}
            containerWidth={containerWidth}
            processedData={processedData}
            valueFormatter={(value) => formatKWh(value)}
          />
        </div>
      )}
    </div>
  );
};

export default HeatingEnergyByDevice;